import type { FC } from 'react';
import { useState } from 'react';
import { Icon } from '@iconify-icon/react';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';

const TermsConditionPage: FC = () => {
  const [activeSection, setActiveSection] = useState('introduction');

  const sections = [
    { id: 'introduction', title: '1. Introduction' },
    { id: 'definitions', title: '2. Definitions' },
    { id: 'account', title: '3. Account Registration' },
    { id: 'use-of-services', title: '4. Use of Services' },
    { id: 'acceptable-use', title: '5. Acceptable Use' },
    { id: 'ai-addendum', title: '6. AI Addendum' },
    { id: 'fees', title: '7. Fees and Payment' },
    { id: 'proprietary-rights', title: '8. Proprietary Rights' },
    { id: 'data-privacy', title: '9. Data Privacy' },
    { id: 'confidentiality', title: '10. Confidentiality' },
    { id: 'warranties', title: '11. Warranties' },
    { id: 'indemnification', title: '12. Indemnification' },
    { id: 'liability', title: '13. Limitation of Liability' },
    { id: 'termination', title: '14. Term and Termination' },
    { id: 'sla', title: '15. Service Level Agreement' },
    { id: 'general', title: '16. General Provisions' },
    { id: 'contact', title: '17. Contact Information' },
  ];

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -100;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <NextSeo
        title="Terms of Service"
        description="Review the terms and conditions governing your use of Chat.co's AI-powered chatbot platform and services."
      />
      <Header />

      {/* Hero Section */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-md bg-primary-100 px-3 py-1.5">
              <Icon icon="tabler:file-text" className="text-base text-primary-700" />
              <span className="text-xs font-semibold uppercase tracking-wider text-primary-700">
                Legal
              </span>
            </div>
            <h1 className="mb-4 text-5xl font-bold tracking-tight text-gray-900">
              Terms of Service
            </h1>
            <p className="text-lg text-gray-600">Effective Date: September 25, 2026</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex gap-12 py-12">
          {/* Sidebar Navigation */}
          <aside className="hidden w-64 flex-shrink-0 lg:block">
            <div className="sticky top-24">
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`block w-full rounded-md px-3 py-2 text-left text-sm font-medium transition-colors ${
                      activeSection === section.id
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}>
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <main className="max-w-3xl flex-1">
            <div className="prose prose-slate max-w-none">
              {/* Section 1: Introduction and Acceptance */}
              <section id="introduction" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  1. Introduction and Acceptance
                </h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  1.1 Agreement Overview
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  These Terms of Service ("Terms") constitute a binding legal agreement between SA
                  Cyber LLC, a Wyoming limited liability company doing business as "Chat.co"
                  ("Chat.co," "we," "us," or "our") and the individual or legal entity accepting
                  these Terms ("Customer" or "you"). These Terms govern your access to and use of
                  our AI-powered chatbot creation platform, knowledge base management system,
                  embeddable chat widgets, artificial intelligence tools, proprietary software, and
                  all related services (collectively, the "Services").
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  1.2 Acceptance of Terms
                </h3>
                <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4">
                  <p className="text-base font-semibold leading-relaxed text-gray-800">
                    BY ACCEPTING THESE TERMS, EITHER BY CLICKING A BOX INDICATING YOUR ACCEPTANCE,
                    EXECUTING AN ORDER FORM THAT REFERENCES THESE TERMS, CREATING AN ACCOUNT, OR
                    USING THE SERVICES, YOU AGREE TO BE BOUND BY THESE TERMS. IF YOU DO NOT AGREE TO
                    THESE TERMS, YOU MAY NOT ACCESS OR USE THE SERVICES.
                  </p>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">1.3 Authority to Bind</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  If you are entering into these Terms on behalf of a company, organization, or
                  other legal entity, you represent and warrant that you have the authority to bind
                  such entity and its affiliates to these Terms. In such case, the terms "Customer"
                  or "you" shall refer to such entity and its affiliates. If you do not have such
                  authority, or if you do not agree with these Terms, you must not accept these
                  Terms and may not use the Services.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  1.4 Modifications to Terms
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Chat.co reserves the right to modify these Terms at any time. We will provide
                  notice of material changes by posting the updated Terms on our website and
                  updating the "Effective Date" above. Your continued use of the Services after such
                  modifications constitutes your acceptance of the revised Terms. If you do not
                  agree to the modified Terms, you must stop using the Services and terminate your
                  account.
                </p>
              </section>

              {/* Section 2: Definitions */}
              <section id="definitions" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">2. Definitions</h2>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  The following terms shall have the meanings set forth below:
                </p>
                <div className="space-y-4">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-base leading-relaxed text-gray-700">
                      <strong>"Affiliate"</strong> means any entity that directly or indirectly
                      controls, is controlled by, or is under common control with the subject
                      entity. "Control" means direct or indirect ownership or control of more than
                      50% of the voting interests of the subject entity.
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-base leading-relaxed text-gray-700">
                      <strong>"Authorized User"</strong> means an individual who is authorized by
                      Customer to use the Services, for whom a subscription to the Services has been
                      ordered, and who has been supplied user identifications and passwords by
                      Customer (or by us at Customer's request). Authorized Users may include
                      employees, consultants, contractors, and agents of Customer, as well as
                      Customer's own end-user customers who interact with Customer's deployed
                      Chatbots ("End Users").
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-base leading-relaxed text-gray-700">
                      <strong>"Chatbot"</strong> means an AI-powered conversational agent created by
                      Customer using the Services, which may be trained on Customer's Knowledge Base
                      and deployed via embeddable widgets or hosted pages.
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-base leading-relaxed text-gray-700">
                      <strong>"Customer Data"</strong> means all electronic data, documents, text,
                      images, messages, sound, video, and information submitted by Customer or
                      Authorized Users to the Services, including without limitation documents
                      uploaded to Knowledge Bases, Chatbot configurations, conversation logs, and
                      any Input provided to AI Features.
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-base leading-relaxed text-gray-700">
                      <strong>"AI Features"</strong> means features of the Services that utilize
                      artificial intelligence, machine learning, large language models, or similar
                      technologies to generate outputs ("Output") based on inputs provided by
                      Customer or Authorized Users ("Input"). AI Features include, but are not
                      limited to, automated chatbot responses, document analysis, and natural
                      language processing capabilities.
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-base leading-relaxed text-gray-700">
                      <strong>"Knowledge Base"</strong> means a collection of documents, files, and
                      data uploaded by Customer to the Services for the purpose of training and
                      informing Chatbot responses.
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-base leading-relaxed text-gray-700">
                      <strong>"Personal Data"</strong> means any information relating to an
                      identified or identifiable natural person, as defined under applicable data
                      protection laws including the General Data Protection Regulation (GDPR) and
                      the California Consumer Privacy Act (CCPA).
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-base leading-relaxed text-gray-700">
                      <strong>"Subscription Term"</strong> means the period during which Customer
                      has agreed to subscribe to the Services, as specified in the applicable Order
                      Form.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 3: Account Registration and Security */}
              <section id="account" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  3. Account Registration and Security
                </h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">3.1 Account Creation</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  To access and use the Services, Customer must create an account by providing
                  accurate, current, and complete information as prompted by the registration
                  process. Customer agrees to maintain and promptly update such information to keep
                  it accurate, current, and complete.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">3.2 Account Security</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Customer is responsible for maintaining the confidentiality of all login
                  credentials associated with Customer's account and for all activities that occur
                  under Customer's account. Customer agrees to: (a) immediately notify Chat.co of
                  any unauthorized use of Customer's account or any other breach of security; and
                  (b) ensure that Customer exits from the account at the end of each session.
                  Chat.co shall not be liable for any loss or damage arising from Customer's failure
                  to comply with this section.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  3.3 Authorized User Management
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Customer is solely responsible for:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Designating which individuals are Authorized Users</li>
                  <li>Ensuring that Authorized Users comply with these Terms</li>
                  <li>Controlling the level of access each Authorized User has to the Services</li>
                  <li>
                    Promptly revoking access for any Authorized User who is no longer authorized or
                    who violates these Terms
                  </li>
                </ul>
                <p className="mt-3 text-base leading-relaxed text-gray-700">
                  Customer shall be liable for all acts and omissions of its Authorized Users.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">3.4 Age Requirement</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  The Services are not intended for use by individuals under the age of 18. By using
                  the Services, you represent and warrant that you are at least 18 years of age. If
                  we learn that we have collected or received Personal Data from an individual under
                  18, we will delete that information.
                </p>
              </section>

              {/* Section 4: Use of Services and Restrictions */}
              <section id="use-of-services" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  4. Use of Services and Restrictions
                </h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">4.1 License Grant</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Subject to these Terms and the applicable Order Form, Chat.co grants Customer a
                  limited, non-exclusive, non-transferable (except as permitted in Section 16.6),
                  non-sublicensable right to access and use the Services and Documentation during
                  the Subscription Term solely for Customer's internal business purposes and, where
                  applicable, to provide services to Customer's own clients through deployed
                  Chatbots.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  4.2 Service Description
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  The Services provide Customer with tools to:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Upload documents and create Knowledge Bases</li>
                  <li>Build and configure AI-powered Chatbots trained on Customer's data</li>
                  <li>Deploy Chatbots via embeddable JavaScript widgets or hosted chat pages</li>
                  <li>Manage conversations and view analytics</li>
                  <li>
                    Access other features as described in the Documentation and applicable Order
                    Form
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">4.3 Restrictions</h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Customer will not, and will not permit any Authorized User or third party to:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Make any Service available to anyone other than Authorized Users, or use any
                    Service for the benefit of anyone other than Customer, unless expressly stated
                    otherwise in an Order Form
                  </li>
                  <li>
                    Sell, resell, license, sublicense, distribute, rent, or lease any Service or
                    Content
                  </li>
                  <li>
                    Use a Service to store or transmit infringing, libelous, defamatory, or
                    otherwise unlawful or tortious material
                  </li>
                  <li>Use a Service to store or transmit Malicious Code</li>
                  <li>Interfere with or disrupt the integrity or performance of any Service</li>
                  <li>
                    Attempt to gain unauthorized access to any Service or its related systems or
                    networks
                  </li>
                  <li>Copy, modify, or create derivative works of the Services</li>
                  <li>
                    Reverse engineer, disassemble, decompile, or otherwise attempt to derive the
                    source code
                  </li>
                  <li>Access any Service in order to build a competitive product or service</li>
                  <li>
                    Use automated means to access the Services, except through our published API
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  4.4 Customer Responsibilities
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Customer will be responsible for:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Authorized Users' compliance with these Terms, the Documentation, and applicable
                    laws
                  </li>
                  <li>The accuracy, quality, legality, and appropriateness of Customer Data</li>
                  <li>Using commercially reasonable efforts to prevent unauthorized access</li>
                  <li>Notifying Chat.co promptly of any unauthorized access or use</li>
                  <li>
                    Ensuring compliance with all applicable laws and regulations, including export
                    control laws
                  </li>
                </ul>
                <h3
                  id="external-connections"
                  className="mb-4 mt-8 scroll-mt-24 text-xl font-bold text-gray-900">
                  4.5 Connecting External Applications
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  You may connect a supported external application to the experts and knowledge
                  available in your Chat.co account through the provided sign-in and consent flow.
                  You select the workspace and billing context and authorize the application to make
                  requests within the displayed permissions. Your current account, seat, license,
                  expert, and document permissions continue to apply to each request.
                </p>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  The connection supports expert questions and follow-ups, authorized document
                  searches, and access to supporting citations and documents. Available capabilities
                  depend on the expert configured in your account. Expert consultation and its
                  supporting citations do not require a separate knowledge-management permission;
                  independent knowledge search requires the applicable knowledge-reading permission.
                  Connecting does not grant account-management rights or unrestricted document
                  access.
                </p>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  You are responsible for choosing the external applications you connect and for
                  having authority to share the information requested through them. Their own terms,
                  privacy policies, and any separate charges apply to their services. Directory
                  availability and approval are controlled by the external platform.
                </p>
              </section>

              {/* Section 5: Acceptable Use Policy */}
              <section id="acceptable-use" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">5. Acceptable Use Policy</h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">5.1 Prohibited Uses</h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Customer agrees not to use the Services, and will ensure that Authorized Users do
                  not use the Services, to:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Engage in any illegal activities or promote illegal acts</li>
                  <li>Send unsolicited bulk communications, spam, or phishing messages</li>
                  <li>Harass, threaten, intimidate, or bully any person</li>
                  <li>Misrepresent identity or affiliation with any person or entity</li>
                  <li>
                    Transmit or store content that is obscene, pornographic, or harmful to minors
                  </li>
                  <li>Promote violence, hatred, or discrimination</li>
                  <li>
                    Infringe upon intellectual property rights, privacy rights, or publicity rights
                  </li>
                  <li>Distribute malware, viruses, or other harmful code</li>
                  <li>Attempt to circumvent security measures or gain unauthorized access</li>
                  <li>
                    Use the Services to provide professional advice without appropriate licensure
                  </li>
                  <li>
                    Use the Services in a manner that could damage or impair Chat.co's servers or
                    networks
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">5.2 Enforcement</h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Chat.co reserves the right, but does not assume the obligation, to investigate any
                  violation of this Acceptable Use Policy or misuse of the Services. Chat.co may, in
                  its sole discretion:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Remove or refuse to post any Customer Data</li>
                  <li>Suspend or terminate Customer's access to the Services</li>
                  <li>
                    Report any activity that it suspects violates any law to appropriate law
                    enforcement officials
                  </li>
                  <li>
                    Take any other action Chat.co deems appropriate to protect its rights or the
                    rights of third parties
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  5.3 Content Moderation
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  While Chat.co does not routinely monitor Customer Data, Chat.co reserves the right
                  to review Customer Data for compliance with these Terms and applicable law.
                  Customer acknowledges that Chat.co may use automated systems to scan for Malicious
                  Code, prohibited content, or other violations.
                </p>
              </section>

              {/* Section 6: Artificial Intelligence Addendum */}
              <section id="ai-addendum" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  6. Artificial Intelligence Addendum
                </h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  6.1 AI Features Overview
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  The Services incorporate AI Features powered by large language models and machine
                  learning technologies. These AI Features enable Customer to create intelligent
                  Chatbots that can understand natural language, process documents, and generate
                  contextually relevant responses based on Customer's Knowledge Base and Input.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  6.2 Input and Output Ownership
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  As between the parties, and to the extent permitted by applicable law:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    <strong>Customer owns all Input.</strong> Customer retains all right, title, and
                    interest in and to Input, including any intellectual property rights therein.
                  </li>
                  <li>
                    Subject to Customer's compliance with these Terms, Chat.co hereby assigns to
                    Customer all of Chat.co's right, title, and interest in and to the Output. This
                    assignment is effective upon creation of the Output.
                  </li>
                  <li>
                    Customer may use Output for any lawful purpose, including commercial purposes
                    such as sale or publication, provided Customer complies with these Terms.
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  6.3 AI Accuracy Disclaimer ("Hallucinations")
                </h3>
                <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4">
                  <p className="mb-3 text-base font-semibold leading-relaxed text-gray-800">
                    ARTIFICIAL INTELLIGENCE AND MACHINE LEARNING ARE RAPIDLY EVOLVING FIELDS OF
                    STUDY. CUSTOMER ACKNOWLEDGES AND AGREES THAT:
                  </p>
                  <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                    <li>
                      Output may not be unique across users, and the Services may generate the same
                      or similar Output for Chat.co or third parties based on similar Input
                    </li>
                    <li>
                      AI Features may generate Output that is incorrect, incomplete, inconsistent,
                      misleading, or offensive ("Hallucinations")
                    </li>
                    <li>
                      AI Features are probabilistic in nature and may produce different outputs in
                      response to identical inputs
                    </li>
                    <li>
                      Customer is solely responsible for evaluating and verifying the accuracy,
                      completeness, and appropriateness of Output
                    </li>
                    <li>Customer assumes all responsibility for decisions made based on Output</li>
                  </ul>
                  <p className="mt-4 text-base font-semibold leading-relaxed text-gray-800">
                    CHAT.CO MAKES NO WARRANTY AS TO THE ACCURACY, RELIABILITY, COMPLETENESS, OR
                    SUITABILITY OF ANY AI-GENERATED OUTPUT. THE AI FEATURES ARE PROVIDED "AS IS"
                    WITHOUT WARRANTY OF ANY KIND.
                  </p>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  6.4 Human-in-the-Loop Requirement
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Customer agrees to implement appropriate human review and oversight of
                  AI-generated Output before deploying it in production environments, particularly
                  for high-stakes use cases. Customer acknowledges that AI Features are intended to
                  assist, not replace, human judgment and decision-making.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  6.5 No Professional Advice
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  The AI Features are not intended to provide, and should not be relied upon for,
                  legal, medical, financial, tax, accounting, or other professional advice. Customer
                  should consult with a qualified professional before making any decisions based on
                  Output. If Customer deploys Chatbots that may interact with End Users seeking such
                  advice, Customer is solely responsible for ensuring appropriate disclaimers and
                  disclosures are provided.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  6.6 Training Data Rights
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Chat.co will not use Customer Data to train or improve its foundation artificial
                  intelligence models that are generally available to other customers without
                  Customer's express written permission. However, Chat.co may:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Use Customer Data to provide, maintain, and improve the Services for Customer's
                    account specifically
                  </li>
                  <li>
                    Use usage patterns, metadata, aggregate statistics, and anonymized/de-identified
                    data to improve the Services generally
                  </li>
                  <li>
                    Use Customer Data as necessary to detect and prevent fraud, abuse, security
                    threats, and violations of these Terms
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  6.7 Third-Party AI Services
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  The AI Features may incorporate or rely upon artificial intelligence services
                  provided by third parties (e.g., AWS Bedrock, OpenAI, Anthropic). Customer's use
                  of the Services is subject to the terms and policies of such third-party providers
                  to the extent applicable. Chat.co shall not be liable for any acts or omissions of
                  third-party AI service providers.
                </p>
              </section>

              {/* Section 7: Fees and Payment */}
              <section id="fees" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">7. Fees and Payment</h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">7.1 Fees</h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Customer will pay all fees specified in Order Forms. Except as otherwise specified
                  herein or in an Order Form:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Fees are based on Services and subscription tiers purchased and not actual
                    usage, unless a usage-based pricing model is specified
                  </li>
                  <li>Payment obligations are non-cancelable and fees paid are non-refundable</li>
                  <li>
                    Quantities purchased cannot be decreased during the relevant Subscription Term
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  7.2 Subscription Tiers
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  The Services are offered in various subscription tiers (e.g., Starter,
                  Professional, Enterprise) with different feature sets, usage limits, and pricing.
                  The specific features, limits, and pricing for each tier are set forth in the
                  Order Form or on the Chat.co pricing page. Customer's access to features and
                  capacity is limited to those included in Customer's selected subscription tier.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  7.2.1 Self-Service Trials and Voice Bundles
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Eligible new self-service subscriptions begin with a 14-day free trial. A payment
                  method and subscription confirmation are required. The amount due today, selected
                  plan, billing interval, trial end and recurring price are shown at checkout.
                  Unless you cancel in Billing before the trial ends, your subscription
                  automatically renews at the displayed price, plus applicable taxes, monthly or
                  annually as selected. Canceling stops future renewal; it does not erase amounts
                  already owed. Enterprise subscriptions and negotiated agreements follow their
                  applicable Order Form.
                </p>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  A Chat + Voice bundle includes the selected Chat.co plan, the selected VoiceLine
                  tier and one phone number, subject to number availability. All VoiceLine bundle
                  trials include 200 voice minutes for the trial, with no overage or automatic
                  refill. After the trial, the paid tier includes 200, 750 or 2,000 voice minutes
                  per month. Yearly bundles are billed annually and their voice allowance refreshes
                  monthly; unused voice minutes do not roll over. The trial does not start again
                  when you change plans. Existing standalone subscriptions retain their agreed
                  pricing unless you confirm a change. Phone-number availability and supported
                  regions can vary.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  7.3 Invoicing and Payment
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Fees will be invoiced in advance and otherwise in accordance with the relevant
                  Order Form. Unless otherwise stated in the Order Form:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Fees are due net thirty (30) days from the invoice date for enterprise
                    customers, or on the disclosed billing date for self-service customers after any
                    applicable free trial
                  </li>
                  <li>
                    Customer shall pay all fees via credit card, ACH, or wire transfer as specified
                  </li>
                  <li>
                    Customer is responsible for providing complete and accurate billing and contact
                    information
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">7.4 Overdue Charges</h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  If any invoiced amount is not received by Chat.co by the due date, then without
                  limiting Chat.co's rights or remedies:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Those charges may accrue late interest at the rate of 1.5% of the outstanding
                    balance per month, or the maximum rate permitted by law, whichever is lower
                  </li>
                  <li>
                    Chat.co may suspend access to the Services until such amounts are paid in full
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  7.5 True-Up for Additional Usage
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  If Customer adds Authorized Users, Chatbots, or other billable items beyond the
                  quantities included in Customer's subscription tier during a billing cycle,
                  Chat.co will charge Customer for the applicable fees prorated for the remainder of
                  the then-current billing cycle. Chat.co may conduct periodic audits to verify
                  Customer's usage and reconcile any underpayment.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">7.6 Taxes</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  All fees are exclusive of taxes. Customer is responsible for paying all taxes,
                  levies, or duties assessed by taxing authorities in connection with Customer's
                  purchase of the Services, excluding taxes based on Chat.co's net income. If
                  Chat.co has a legal obligation to pay or collect taxes for which Customer is
                  responsible, Chat.co will invoice Customer and Customer will pay that amount
                  unless Customer provides Chat.co with a valid tax exemption certificate.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">7.7 Price Changes</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Chat.co may change its pricing at any time. Any price changes will take effect
                  upon the next renewal of Customer's subscription. Chat.co will provide Customer
                  with reasonable advance notice of any price increases.
                </p>
                <h3
                  id="connector-credits"
                  className="mb-4 mt-8 scroll-mt-24 text-xl font-bold text-gray-900">
                  7.8 Credits Used by External Connections
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Asking an expert through a connected application uses that expert’s normal Chat.co
                  message-credit rate and existing allocation in the selected workspace. Retrieval
                  performed inside the expert’s answer has no additional standalone-search charge.
                </p>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Each completed standalone document search uses 0.5 message credit, including a
                  search that runs successfully but finds no matching results. Failed searches and
                  inaccessible expert selections are not charged. A retry of the same identified
                  operation is not charged twice. Listing available experts, fetching an existing
                  citation, and opening returned evidence do not use additional message credits.
                </p>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  New paid requests are blocked when the applicable allocation is insufficient.
                  Expert listing and access to previously returned evidence remain available while
                  current authorization and source availability permit. The connector has no
                  checkout or automatic upgrade flow; manage your Chat.co plan and allocations
                  through your account.
                </p>
              </section>

              {/* Section 8: Proprietary Rights and Licenses */}
              <section id="proprietary-rights" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  8. Proprietary Rights and Licenses
                </h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  8.1 Reservation of Rights
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Subject to the limited rights expressly granted hereunder, Chat.co and its
                  affiliates, licensors, and content providers reserve all of their right, title,
                  and interest in and to the Services, including all of their related intellectual
                  property rights. Without limiting the foregoing, Chat.co retains all rights in and
                  to:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    The underlying software, algorithms, and technologies powering the Services
                  </li>
                  <li>The Chat.co name, logo, trademarks, and brand features</li>
                  <li>
                    Any improvements, modifications, or derivative works of the Services, regardless
                    of who suggests them
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  8.2 Customer Data License
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Customer grants Chat.co, its Affiliates, and applicable contractors a worldwide,
                  limited-term license to host, copy, process, transmit, and display Customer Data
                  as reasonably necessary for Chat.co to:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Provide, maintain, and improve the Services in accordance with this Agreement
                  </li>
                  <li>Ensure proper operation of the Services and associated systems</li>
                  <li>Comply with applicable law or legal process</li>
                </ul>
                <p className="mt-3 text-base leading-relaxed text-gray-700">
                  Subject to the limited licenses granted herein, Chat.co acquires no right, title,
                  or interest from Customer or its licensors under this Agreement in or to any
                  Customer Data.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">8.3 Feedback</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  If Customer or any Authorized User provides Chat.co with any feedback,
                  suggestions, ideas, or recommendations regarding the Services ("Feedback"),
                  Chat.co shall have a royalty-free, worldwide, irrevocable, perpetual license to
                  use, incorporate, and otherwise exploit such Feedback for any purpose without any
                  obligation or compensation to Customer.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">8.4 Usage Data</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Chat.co may collect and use Usage Data (technical logs, performance metrics,
                  aggregate usage statistics, and similar operational data) to: (a) operate and
                  improve the Services; (b) develop new products and features; (c) generate
                  aggregate insights and benchmarks; and (d) for other lawful business purposes.
                  Usage Data does not include Customer Data or Personal Data and shall be owned by
                  Chat.co.
                </p>
              </section>

              {/* Section 9: Data Privacy and Security */}
              <section id="data-privacy" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  9. Data Privacy and Security
                </h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  9.1 Data Processing Addendum
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  To the extent that Chat.co processes any Personal Data contained in Customer Data
                  on Customer's behalf in the provision of the Services, the terms of the Data
                  Processing Addendum ("DPA"), which is available at chat.co/legal/dpa and
                  incorporated herein by reference, shall apply. For the purposes of the Standard
                  Contractual Clauses (where applicable), Customer and its applicable Affiliates are
                  each the data exporter.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  9.2 Roles and Responsibilities
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  With respect to Personal Data contained in Customer Data:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Customer is the data controller (or "business" under CCPA) and determines the
                    purposes and means of processing
                  </li>
                  <li>
                    Chat.co is the data processor (or "service provider" under CCPA) and processes
                    Personal Data only in accordance with Customer's documented instructions and
                    these Terms
                  </li>
                  <li>
                    Customer is responsible for ensuring that it has obtained all necessary
                    consents, provided all required notices, and has a lawful basis for transferring
                    Personal Data to Chat.co
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">9.3 Security Measures</h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Chat.co will implement and maintain appropriate administrative, physical, and
                  technical safeguards designed to protect the security, confidentiality, and
                  integrity of Customer Data. These measures include, but are not limited to:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Access controls and authentication mechanisms</li>
                  <li>Regular security assessments and penetration testing</li>
                  <li>Incident response procedures</li>
                  <li>Employee security training</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  9.4 CCPA Service Provider
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  If Customer is subject to the California Consumer Privacy Act (CCPA) and
                  California Privacy Rights Act (CPRA), the parties agree that Chat.co is a "Service
                  Provider" as defined therein. Chat.co shall not:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Sell or share Customer's Personal Data</li>
                  <li>
                    Retain, use, or disclose Customer's Personal Data for any purpose other than
                    performing the Services specified in this Agreement
                  </li>
                  <li>
                    Retain, use, or disclose such information outside of the direct business
                    relationship between Chat.co and Customer, except as permitted by the CCPA/CPRA
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  9.5 Data Breach Notification
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  In the event that Chat.co becomes aware of a security breach that results in the
                  unauthorized access, acquisition, or disclosure of Customer Data ("Security
                  Incident"), Chat.co will:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Notify Customer of the Security Incident without undue delay</li>
                  <li>
                    Investigate the Security Incident and take reasonable steps to mitigate its
                    effects
                  </li>
                  <li>
                    Provide Customer with information reasonably necessary for Customer to meet its
                    legal obligations
                  </li>
                  <li>Cooperate with Customer in responding to the Security Incident</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">9.6 Data Location</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Customer Data may be processed and stored in the United States or other
                  jurisdictions where Chat.co or its sub-processors maintain facilities. By using
                  the Services, Customer consents to the transfer of Customer Data to such
                  locations. Chat.co will ensure that appropriate safeguards are in place for any
                  international data transfers, including Standard Contractual Clauses where
                  required.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  9.7 Data Retention and Deletion
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Upon termination or expiration of this Agreement, Chat.co will, upon Customer's
                  request, delete or return all Customer Data in its possession within thirty (30)
                  days, except to the extent Chat.co is required by applicable law to retain such
                  data. Customer is responsible for exporting any Customer Data it wishes to retain
                  prior to termination.
                </p>
                <h3
                  id="connector-data"
                  className="mb-4 mt-8 scroll-mt-24 text-xl font-bold text-gray-900">
                  9.8 Data Shared Through External Connections
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  To fulfill an authorized request, Chat.co receives the question or search and
                  returns the relevant answer, excerpts, citations, document links, and operational
                  information to the connected application. Chat.co records consultations, source
                  references, and credit usage as explained in the{' '}
                  <Link
                    href="/privacy-policy#external-connections"
                    className="text-primary-600 underline">
                    Privacy Policy
                  </Link>
                  . Requests use the expert’s configured model provider and any reviewed functions
                  needed for the request.
                </p>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  You can disconnect an application through{' '}
                  <Link href="/connect" className="text-primary-600 underline">
                    Connected applications
                  </Link>
                  . This stops future connector access but does not retract information already
                  delivered to that application, delete its copies, cancel your Chat.co
                  subscription, or erase existing Chat.co conversations and billing records. Private
                  supporting-document links are checked against current Chat.co authorization and
                  source availability when opened. For deletion of data held by another provider,
                  use that provider’s deletion process.
                </p>

                <h3
                  id="website-analytics"
                  className="mb-4 mt-8 scroll-mt-24 text-xl font-bold text-gray-900">
                  9.9 Website and Mobile App Analytics
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  We use Google Analytics 4 to understand website traffic and usage and improve our
                  Services. With your consent, Google Analytics uses cookies and similar
                  technologies to collect information such as pages visited, interactions, session
                  activity, referral information, browser and device details, approximate location,
                  and cookie identifiers. Google processes this information to provide analytics
                  reports. These identifiers are pseudonymous; the underlying data is not
                  necessarily anonymous.
                </p>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  On our website, Google Analytics and Google Ads tags load only after you accept
                  optional cookies. You may decline or withdraw your consent using Cookie Settings
                  in the website footer. Declining optional cookies does not prevent you from using
                  the Services. Acceptance of these Terms or continued use of the Services does not
                  itself constitute consent to optional cookies.
                </p>
                <p className="text-base leading-relaxed text-gray-700">
                  Our mobile apps use Google Analytics for Firebase only after you opt in. You may
                  decline or turn it off using Usage analytics in the app&apos;s Account screen.
                  Accepting these Terms does not constitute consent to mobile analytics.
                </p>
                <p className="text-base leading-relaxed text-gray-700">
                  For details about analytics, advertising cookies, Google&apos;s processing, and
                  your choices, see Section 9 of our{' '}
                  <Link href="/privacy-policy#cookies" className="text-primary-600 underline">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </section>

              {/* Section 10: Confidentiality */}
              <section id="confidentiality" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">10. Confidentiality</h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  10.1 Definition of Confidential Information
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  "Confidential Information" means all information disclosed by a party ("Disclosing
                  Party") to the other party ("Receiving Party"), whether orally or in writing, that
                  is designated as confidential or that reasonably should be understood to be
                  confidential given the nature of the information and the circumstances of
                  disclosure. Confidential Information includes, but is not limited to:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Customer Data (as Customer's Confidential Information)</li>
                  <li>
                    The Services, software, algorithms, and technical documentation (as Chat.co's
                    Confidential Information)
                  </li>
                  <li>The terms, conditions, and pricing of this Agreement and all Order Forms</li>
                  <li>Business plans, strategies, and financial information</li>
                  <li>Any other information marked "confidential" or similar designation</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">10.2 Exclusions</h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Confidential Information does not include information that:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Is or becomes publicly available without breach of any obligation owed to the
                    Disclosing Party
                  </li>
                  <li>
                    Was known to the Receiving Party prior to its disclosure by the Disclosing Party
                  </li>
                  <li>
                    Is received from a third party without breach of any obligation owed to the
                    Disclosing Party
                  </li>
                  <li>
                    Was independently developed by the Receiving Party without use of or reference
                    to the Disclosing Party's Confidential Information
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  10.3 Protection of Confidential Information
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  The Receiving Party will:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Use the same degree of care to protect the Disclosing Party's Confidential
                    Information as it uses to protect its own confidential information of like kind,
                    but not less than reasonable care
                  </li>
                  <li>
                    Not use any Confidential Information of the Disclosing Party for any purpose
                    outside the scope of this Agreement
                  </li>
                  <li>
                    Limit access to Confidential Information to those who need that access for
                    purposes consistent with this Agreement
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  10.4 Compelled Disclosure
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  If the Receiving Party is compelled by law to disclose Confidential Information of
                  the Disclosing Party, it shall provide the Disclosing Party with prior notice of
                  such compelled disclosure (to the extent legally permitted) and reasonable
                  assistance, at the Disclosing Party's expense, if the Disclosing Party wishes to
                  contest the disclosure.
                </p>
              </section>

              {/* Section 11: Warranties and Disclaimers */}
              <section id="warranties" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  11. Warranties and Disclaimers
                </h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  11.1 Mutual Representations
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Each party represents and warrants that:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>It has the legal power and authority to enter into this Agreement</li>
                  <li>
                    This Agreement has been duly executed and authorized and constitutes a binding
                    obligation
                  </li>
                  <li>
                    Its performance under this Agreement will not conflict with any other agreement
                    to which it is bound
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">11.2 Chat.co Warranty</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Chat.co warrants that during an applicable Subscription Term: (a) the Services
                  will perform materially in accordance with the applicable Documentation; and (b)
                  the functionality of the Services will not be materially decreased during a
                  Subscription Term. For any breach of this warranty, Customer's exclusive remedy
                  shall be as set forth in Section 14.3 (Termination for Cause).
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  11.3 Customer Warranties
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Customer represents and warrants that:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Customer has obtained all rights, licenses, and consents necessary to grant the
                    licenses to Customer Data under this Agreement
                  </li>
                  <li>
                    Customer Data does not and will not infringe, misappropriate, or violate the
                    intellectual property rights, privacy rights, or other rights of any third party
                  </li>
                  <li>
                    Customer's use of the Services will comply with all applicable laws,
                    regulations, and industry standards
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">11.4 Disclaimers</h3>
                <div className="rounded-lg border-l-4 border-red-500 bg-red-50 p-4">
                  <p className="mb-3 text-base font-semibold leading-relaxed text-gray-800">
                    EXCEPT AS EXPRESSLY PROVIDED HEREIN, NEITHER PARTY MAKES ANY WARRANTY OF ANY
                    KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, AND EACH PARTY
                    SPECIFICALLY DISCLAIMS ALL IMPLIED WARRANTIES, INCLUDING ANY IMPLIED WARRANTY OF
                    MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, OR NON-INFRINGEMENT,
                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW.
                  </p>
                  <p className="text-base leading-relaxed text-gray-700">
                    THE SERVICES, INCLUDING ALL AI FEATURES AND BETA FEATURES, ARE PROVIDED "AS IS"
                    AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND. CHAT.CO DOES NOT WARRANT THAT:
                    (A) THE SERVICES WILL BE UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE; (B) THE
                    RESULTS OBTAINED FROM USE OF THE SERVICES WILL BE ACCURATE OR RELIABLE; (C) THE
                    QUALITY OF ANY PRODUCTS, SERVICES, INFORMATION, OR OTHER MATERIAL OBTAINED
                    THROUGH THE SERVICES WILL MEET CUSTOMER'S EXPECTATIONS; OR (D) ANY ERRORS IN THE
                    SERVICES WILL BE CORRECTED.
                  </p>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">11.5 Beta Services</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Chat.co may offer certain features or services designated as "beta," "preview,"
                  "early access," or similar ("Beta Services"). Beta Services are provided for
                  evaluation purposes only and may be discontinued at any time without notice.
                  Customer's use of Beta Services is at Customer's sole risk. Beta Services are not
                  covered by any SLA, warranty, or indemnification obligations under this Agreement.
                </p>
              </section>

              {/* Section 12: Indemnification */}
              <section id="indemnification" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">12. Indemnification</h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  12.1 Indemnification by Chat.co
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Chat.co will defend Customer against any claim, demand, suit, or proceeding made
                  or brought against Customer by a third party alleging that the Services (excluding
                  Customer Data, Third-Party Applications, and AI-generated Output) infringe or
                  misappropriate such third party's intellectual property rights (a "Claim Against
                  Customer"), and will indemnify Customer from any damages, attorney fees, and costs
                  finally awarded against Customer as a result of, or for amounts paid by Customer
                  under a settlement approved by Chat.co in writing of, a Claim Against Customer.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  12.2 Exclusions from Chat.co Indemnification
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Chat.co shall have no obligation under Section 12.1 to the extent a claim arises
                  from:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Customer Data</li>
                  <li>Third-Party Applications</li>
                  <li>Output generated by AI Features</li>
                  <li>Modifications to the Services not made by Chat.co</li>
                  <li>Customer's use of the Services in violation of these Terms</li>
                  <li>
                    Combination of the Services with other products, services, or technologies not
                    provided by Chat.co
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">12.3 Remediation</h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  If the Services are, or in Chat.co's reasonable opinion are likely to be, the
                  subject of an infringement claim, Chat.co may, at its option and expense:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Procure for Customer the right to continue using the Services</li>
                  <li>
                    Replace or modify the Services so they become non-infringing without materially
                    reducing functionality
                  </li>
                  <li>
                    If the foregoing options are not reasonably practicable, terminate Customer's
                    access to the affected Services and refund any prepaid fees for the remainder of
                    the Subscription Term
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  12.4 Indemnification by Customer
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Customer will defend Chat.co against any claim, demand, suit, or proceeding made
                  or brought against Chat.co by a third party alleging that:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Customer Data, or Customer's use of Customer Data with the Services, infringes
                    or misappropriates such third party's intellectual property rights or violates
                    applicable law
                  </li>
                  <li>
                    A Third-Party Application provided or specified by Customer infringes or
                    misappropriates such third party's intellectual property rights
                  </li>
                  <li>
                    Customer's use of the Services in violation of these Terms caused harm to such
                    third party
                  </li>
                  <li>
                    Output generated based on Customer's Input infringes or misappropriates such
                    third party's intellectual property rights
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">12.5 Exclusive Remedy</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  This Section 12 states the indemnifying party's sole liability to, and the
                  indemnified party's exclusive remedy against, the other party for any type of
                  claim described in this Section.
                </p>
              </section>

              {/* Section 13: Limitation of Liability */}
              <section id="liability" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  13. Limitation of Liability
                </h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  13.1 Limitation of Liability
                </h3>
                <div className="rounded-lg border-l-4 border-red-500 bg-red-50 p-4">
                  <p className="text-base font-semibold leading-relaxed text-gray-800">
                    IN NO EVENT SHALL THE AGGREGATE LIABILITY OF EACH PARTY TOGETHER WITH ALL OF ITS
                    AFFILIATES ARISING OUT OF OR RELATED TO THIS AGREEMENT EXCEED THE TOTAL AMOUNT
                    PAID BY CUSTOMER AND ITS AFFILIATES HEREUNDER FOR THE SERVICES GIVING RISE TO
                    THE LIABILITY IN THE TWELVE (12) MONTHS PRECEDING THE FIRST INCIDENT OUT OF
                    WHICH THE LIABILITY AROSE. THE FOREGOING LIMITATION WILL APPLY WHETHER AN ACTION
                    IS IN CONTRACT OR TORT AND REGARDLESS OF THE THEORY OF LIABILITY, BUT WILL NOT
                    LIMIT CUSTOMER'S PAYMENT OBLIGATIONS UNDER SECTION 7.
                  </p>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  13.2 Exclusion of Consequential and Related Damages
                </h3>
                <div className="rounded-lg border-l-4 border-red-500 bg-red-50 p-4">
                  <p className="text-base font-semibold leading-relaxed text-gray-800">
                    IN NO EVENT WILL EITHER PARTY OR ITS AFFILIATES HAVE ANY LIABILITY ARISING OUT
                    OF OR RELATED TO THIS AGREEMENT FOR ANY: (A) LOST PROFITS, LOST REVENUES, LOSS
                    OF GOODWILL, OR LOSS OF ANTICIPATED SAVINGS; (B) LOSS OR CORRUPTION OF DATA; (C)
                    INDIRECT, SPECIAL, INCIDENTAL, CONSEQUENTIAL, COVER, BUSINESS INTERRUPTION, OR
                    PUNITIVE DAMAGES; OR (D) COST OF PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES,
                    WHETHER AN ACTION IS IN CONTRACT OR TORT AND REGARDLESS OF THE THEORY OF
                    LIABILITY, EVEN IF A PARTY OR ITS AFFILIATES HAVE BEEN ADVISED OF THE
                    POSSIBILITY OF SUCH DAMAGES.
                  </p>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">13.3 Exceptions</h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  The limitations in Sections 13.1 and 13.2 shall not apply to:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>A party's breach of its confidentiality obligations under Section 10</li>
                  <li>A party's indemnification obligations under Section 12</li>
                  <li>
                    Customer's breach of Section 4.3 (Restrictions) or Section 5 (Acceptable Use
                    Policy)
                  </li>
                  <li>Fraud, gross negligence, or willful misconduct</li>
                  <li>Amounts owed under Section 7 (Fees and Payment)</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  13.4 Allocation of Risk
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  The parties acknowledge that the limitations of liability in this Section 13 are
                  an essential element of the bargain between the parties and reflect a fair
                  allocation of risk. The fees charged for the Services would be significantly
                  higher without such limitations.
                </p>
              </section>

              {/* Section 14: Term and Termination */}
              <section id="termination" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">14. Term and Termination</h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  14.1 Term of Agreement
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  This Agreement commences on the date Customer first accepts it and continues until
                  all subscriptions hereunder have expired or have been terminated.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  14.2 Term of Subscriptions
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  The term of each subscription shall be as specified in the applicable Order Form.
                  Unless otherwise specified in an Order Form:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Subscriptions will automatically renew for additional periods equal to the
                    expiring Subscription Term or one (1) year, whichever is shorter
                  </li>
                  <li>
                    Either party may give the other written notice (email acceptable) of its intent
                    not to renew at least thirty (30) days before the end of the relevant
                    Subscription Term
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  14.3 Termination for Cause
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Either party may terminate this Agreement for cause:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>
                    Upon thirty (30) days' written notice to the other party of a material breach if
                    such breach remains uncured at the expiration of such period
                  </li>
                  <li>
                    Immediately if the other party becomes the subject of a petition in bankruptcy
                    or any other proceeding relating to insolvency, receivership, liquidation, or
                    assignment for the benefit of creditors
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  14.4 Termination for Convenience
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Customer may terminate a subscription at any time by providing written notice to
                  Chat.co. However, Customer shall not be entitled to any refund of prepaid fees for
                  the remainder of the then-current Subscription Term unless otherwise specified in
                  the applicable Order Form or required by applicable law.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  14.5 Effect of Termination
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Upon termination or expiration of this Agreement:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Customer's right to access and use the Services shall immediately cease</li>
                  <li>
                    Each party will return or destroy, at the other party's option, all Confidential
                    Information of the other party in its possession
                  </li>
                  <li>Chat.co will delete Customer Data in accordance with Section 9.7</li>
                  <li>
                    Any provisions that by their nature should survive termination will survive
                  </li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  14.6 Suspension of Services
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  Chat.co may suspend Customer's access to the Services:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>If Customer fails to pay undisputed fees when due</li>
                  <li>
                    If Customer's use of the Services poses a security risk to the Services or third
                    parties
                  </li>
                  <li>
                    If Customer's use of the Services may subject Chat.co or third parties to
                    liability
                  </li>
                  <li>If Customer is in breach of this Agreement</li>
                  <li>As required by law or legal process</li>
                </ul>
                <p className="mt-3 text-base leading-relaxed text-gray-700">
                  Chat.co will provide Customer with advance notice of suspension where reasonably
                  practicable.
                </p>
              </section>

              {/* Section 15: Service Level Agreement */}
              <section id="sla" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  15. Service Level Agreement
                </h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  15.1 Service Commitment
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Chat.co will use commercially reasonable efforts to make the Services available
                  with a Monthly Uptime Percentage of at least <strong>99.9%</strong> during any
                  monthly billing cycle (the "Service Commitment"). This Service Commitment applies
                  only to paid subscription tiers and does not apply to free trial periods, Beta
                  Services, or Third-Party Applications.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">15.2 Definitions</h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  <strong>"Downtime"</strong> means the total accumulated minutes during a calendar
                  month during which the Services are unavailable to Customer. Unavailability is
                  measured based on server-side error rates and inability to access core
                  functionality.
                </p>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  <strong>"Exclusions"</strong> means unavailability resulting from:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Scheduled maintenance with at least 24 hours' prior notice</li>
                  <li>
                    Emergency maintenance to address security vulnerabilities or critical issues
                  </li>
                  <li>Force majeure events</li>
                  <li>
                    Failures of Customer's equipment, software, network connections, or third-party
                    services
                  </li>
                  <li>Customer's breach of this Agreement</li>
                  <li>Suspension of Customer's account in accordance with this Agreement</li>
                  <li>Third-Party Applications</li>
                  <li>Factors outside of Chat.co's reasonable control</li>
                </ul>
                <p className="mt-3 text-base leading-relaxed text-gray-700">
                  <strong>"Monthly Uptime Percentage"</strong> is calculated as: ((Total Minutes in
                  Month - Downtime Minutes) / Total Minutes in Month) × 100.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">15.3 Service Credits</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  If Chat.co fails to meet the Service Commitment, Customer may be eligible to
                  receive a Service Credit as a percentage of the monthly fees for the affected
                  month.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  15.4 Credit Request and Payment
                </h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  To receive a Service Credit, Customer must submit a request to Chat.co support
                  within thirty (30) days of the end of the month in which the Downtime occurred.
                  The request must include:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>The dates and times of each Downtime incident</li>
                  <li>The affected Services</li>
                  <li>Logs or other documentation corroborating the claimed Downtime</li>
                </ul>
                <p className="mt-3 text-base leading-relaxed text-gray-700">
                  If Chat.co confirms that the Service Commitment was not met, Service Credits will
                  be applied to Customer's next invoice.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">15.5 Maximum Credits</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  The aggregate maximum Service Credits that Customer may receive for any monthly
                  billing period shall not exceed 30% of the fees paid by Customer for the affected
                  Services for that month.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">15.6 Sole Remedy</h3>
                <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4">
                  <p className="text-base font-semibold leading-relaxed text-gray-800">
                    UNLESS OTHERWISE PROVIDED IN THESE TERMS, CUSTOMER'S SOLE AND EXCLUSIVE REMEDY
                    FOR ANY UNAVAILABILITY, NON-PERFORMANCE, OR OTHER FAILURE BY CHAT.CO TO PROVIDE
                    THE SERVICES IS THE RECEIPT OF A SERVICE CREDIT (IF ELIGIBLE) IN ACCORDANCE WITH
                    THE TERMS OF THIS SLA.
                  </p>
                </div>
              </section>

              {/* Section 16: General Provisions */}
              <section id="general" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">16. General Provisions</h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">16.1 Governing Law</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  This Agreement shall be governed by and construed in accordance with the internal
                  laws of the <strong>State of Wyoming</strong>, without regard to its conflict of
                  laws rules. The United Nations Convention on Contracts for the International Sale
                  of Goods shall not apply to this Agreement.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  16.2 Dispute Resolution and Arbitration
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Any controversy or claim arising out of or relating to this Agreement, or the
                  breach thereof, shall be settled by binding arbitration administered by the
                  American Arbitration Association ("AAA") in accordance with its Commercial
                  Arbitration Rules. The arbitration shall take place in Orlando, Florida, unless
                  otherwise agreed by the parties. The arbitrator's decision shall be final and
                  binding, and judgment on the award rendered by the arbitrator may be entered in
                  any court having jurisdiction thereof.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  16.3 Class Action Waiver
                </h3>
                <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4">
                  <p className="text-base font-semibold leading-relaxed text-gray-800">
                    TO THE FULLEST EXTENT PERMITTED BY LAW, CUSTOMER AND CHAT.CO EACH WAIVE THE
                    RIGHT TO A JURY TRIAL AND THE RIGHT TO PARTICIPATE IN A CLASS ACTION, COLLECTIVE
                    ACTION, PRIVATE ATTORNEY GENERAL ACTION, OR OTHER REPRESENTATIVE PROCEEDING OF
                    ANY KIND.
                  </p>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">16.4 DMCA Notice</h3>
                <p className="mb-3 text-base leading-relaxed text-gray-700">
                  If you believe that any content on the Services infringes your copyright, please
                  send a notice of copyright infringement to our Designated Agent at{' '}
                  <a
                    href="mailto:legal@chat.co"
                    className="text-primary-600 hover:text-primary-700">
                    legal@chat.co
                  </a>{' '}
                  in accordance with the Digital Millennium Copyright Act (17 U.S.C. § 512). The
                  notice must include:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Identification of the copyrighted work claimed to have been infringed</li>
                  <li>
                    Identification of the material that is claimed to be infringing and its location
                  </li>
                  <li>Your contact information</li>
                  <li>
                    A statement that you have a good faith belief that use of the material is not
                    authorized
                  </li>
                  <li>A statement that the information in the notice is accurate</li>
                  <li>Your physical or electronic signature</li>
                </ul>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  16.5 Export Compliance
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  The Services may be subject to export laws and regulations of the United States
                  and other jurisdictions. Customer represents that it is not named on any U.S.
                  government denied-party list. Customer will not permit Authorized Users to access
                  or use the Services in a U.S.-embargoed country or in violation of any U.S. export
                  law or regulation.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">16.6 Assignment</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Neither party may assign any of its rights or obligations hereunder, whether by
                  operation of law or otherwise, without the prior written consent of the other
                  party (not to be unreasonably withheld). Notwithstanding the foregoing, either
                  party may assign this Agreement in its entirety, without consent of the other
                  party, in connection with a merger, acquisition, corporate reorganization, or sale
                  of all or substantially all of its assets. Any purported assignment in violation
                  of this section shall be void.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">16.7 Notices</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  All notices under this Agreement shall be in writing and shall be deemed to have
                  been duly given when received, if personally delivered; when receipt is
                  electronically confirmed, if transmitted by email; the day after it is sent, if
                  sent for next day delivery by recognized overnight delivery service; and upon
                  receipt, if sent by certified or registered mail, return receipt requested.
                  Notices to Customer shall be sent to the email address associated with Customer's
                  account. Notices to Chat.co shall be sent to: SA Cyber LLC, Attn: Legal
                  Department,{' '}
                  <a
                    href="mailto:legal@chat.co"
                    className="text-primary-600 hover:text-primary-700">
                    legal@chat.co
                  </a>
                  .
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">16.8 Force Majeure</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Neither party shall be liable for any failure or delay in performing its
                  obligations under this Agreement (except for payment obligations) due to causes
                  beyond its reasonable control, including, but not limited to, acts of God, natural
                  disasters, war, terrorism, riots, embargoes, acts of civil or military
                  authorities, fire, floods, pandemics, strikes, or shortages of transportation,
                  facilities, fuel, energy, labor, or materials.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  16.9 Relationship of the Parties
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  The parties are independent contractors. This Agreement does not create a
                  partnership, franchise, joint venture, agency, fiduciary, or employment
                  relationship between the parties. Neither party has authority to bind the other or
                  incur obligations on the other's behalf.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  16.10 No Third-Party Beneficiaries
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  There are no third-party beneficiaries to this Agreement. This Agreement is
                  intended solely for the benefit of the parties hereto and their respective
                  successors and permitted assigns.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">16.11 Waiver</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  No failure or delay by either party in exercising any right under this Agreement
                  shall constitute a waiver of that right. A waiver of any default shall not be a
                  waiver of any subsequent default.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">16.12 Severability</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  If any provision of this Agreement is held by a court of competent jurisdiction to
                  be contrary to law, the provision will be modified by the court and interpreted so
                  as best to accomplish the objectives of the original provision to the fullest
                  extent permitted by law, and the remaining provisions of this Agreement will
                  remain in effect.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  16.13 Entire Agreement
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  This Agreement, including all Order Forms, the DPA, and any other addenda or
                  exhibits incorporated by reference, constitutes the entire agreement between the
                  parties and supersedes all prior and contemporaneous agreements, proposals, or
                  representations, written or oral, concerning its subject matter. In the event of
                  any conflict between this Agreement and an Order Form, the Order Form shall
                  prevail. No modification, amendment, or waiver of any provision of this Agreement
                  shall be effective unless in writing and signed by both parties.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">16.14 Survival</h3>
                <p className="text-base leading-relaxed text-gray-700">
                  The provisions of this Agreement that by their nature should survive termination
                  or expiration shall survive, including, without limitation, Sections 2
                  (Definitions), 6 (AI Addendum - to the extent relating to Output ownership), 7
                  (Fees and Payment - for amounts accrued), 8 (Proprietary Rights), 10
                  (Confidentiality), 11 (Warranties and Disclaimers), 12 (Indemnification), 13
                  (Limitation of Liability), 14.5 (Effect of Termination), and 16 (General
                  Provisions).
                </p>
              </section>

              {/* Section 17: Contact Information */}
              <section id="contact" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">17. Contact Information</h2>
                <p className="mb-6 text-base leading-relaxed text-gray-700">
                  If you have any questions about these Terms of Service, please contact us at:
                </p>

                <div className="space-y-6 rounded-lg border border-gray-200 bg-gray-50 p-6">
                  <div>
                    <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                      Company
                    </p>
                    <p className="text-base font-medium text-gray-900">
                      SA Cyber LLC d/b/a Chat.co
                    </p>
                  </div>

                  <div>
                    <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                      Legal Inquiries
                    </p>
                    <p className="text-base text-gray-700">
                      Email:{' '}
                      <a
                        href="mailto:legal@chat.co"
                        className="text-primary-600 hover:text-primary-700">
                        legal@chat.co
                      </a>
                    </p>
                  </div>

                  <div>
                    <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                      Support
                    </p>
                    <p className="text-base text-gray-700">
                      Email:{' '}
                      <a
                        href="mailto:support@chat.co"
                        className="text-primary-600 hover:text-primary-700">
                        support@chat.co
                      </a>
                    </p>
                  </div>

                  <div>
                    <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                      Website
                    </p>
                    <p className="text-base text-gray-700">
                      <a
                        href="https://chat.co/contact-us#contact-us"
                        className="text-primary-600 hover:text-primary-700">
                        https://chat.co/contact-us
                      </a>
                    </p>
                  </div>
                </div>

                {/* Final Notice */}
                <div className="mt-8 rounded-lg bg-gray-900 p-6 text-center text-white">
                  <p className="text-lg font-bold">
                    BY USING CHAT.CO, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO
                    BE BOUND BY THESE TERMS OF SERVICE.
                  </p>
                </div>

                <div className="mt-8 border-t border-gray-200 pt-8 text-center">
                  <p className="text-sm text-gray-500">
                    &copy; 2026 SA Cyber LLC d/b/a Chat.co. All rights reserved.
                  </p>
                  <p className="mt-2 text-sm text-gray-500">— END OF TERMS OF SERVICE —</p>
                </div>
              </section>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsConditionPage;
